import { api, type SendConfig } from './api';
import { useTenantStore } from '@/store/tenant.store';
import { canSendAsEvents, isFieldIngestV1Enabled, uploadPositionEvents } from './field-ingest.service';

/**
 * Position record matching the backend positionItemSchema fields.
 * Used for buffering and flushing GPS positions to the server.
 */
export interface PositionRecord {
  /**
   * Client-generated UUID for this GPS point: the server's idempotency key
   * (UNIQUE(session_id, point_id), P0-15). It is created once, when the point
   * is recorded, and travels with the point through the in-memory buffer and
   * the IndexedDB buffer, so every retry of the same point carries the same id
   * and a replayed upload is stored once. Optional only because points
   * buffered by older builds have none; the sync path backfills it from the
   * buffered row's own stable id.
   */
  point_id?: string;
  lat: number;
  lng: number;
  altitude?: number;
  accuracy?: number;
  speed?: number;
  heading?: number;
  /** 0-100; fractional values are accepted by the server and rounded. */
  battery_percent?: number;
  recorded_at: string;
}

/** Server answer for one upload (POST /personnel-tracking/positions). */
export interface PositionUploadResult {
  received: number;
  inserted: number;
  /** Already stored (a replay of points whose earlier response was lost). */
  duplicates: number;
  /** Dropped by the server's recorded_at window; never worth re-sending. */
  rejected: number;
  rejected_reasons?: Record<string, number>;
  clamped?: number;
  last_position_updated?: boolean;
}

/**
 * The server rejects requests over 500 points with a 400. Before this cap,
 * a backlog larger than that, for example after a long stretch offline, was
 * re-sent whole on every sync, rejected every time, and never left the
 * device.
 */
export const MAX_POSITIONS_PER_REQUEST = 500;

/**
 * Flush buffered positions to the server.
 *
 * P1-12: positions go to the lane-2 field endpoint (POST /api/field/v1/events)
 * as `position` events whose event_id is the point's point_id, under the
 * tracker credential (services/field-ingest.service.ts). The legacy route is
 * used when the build flag VITE_FIELD_INGEST_LEGACY=true is set, when a point
 * has no UUID point_id (never written by current builds), or when the server
 * has no field endpoint (404).
 *
 * This thin service function exists to keep API calls out of the
 * Zustand store per project convention. Callers with more than
 * MAX_POSITIONS_PER_REQUEST points must use uploadPositionsInChunks().
 */
export async function flushPositions(
  sessionId: string,
  points: PositionRecord[],
  config?: SendConfig
): Promise<PositionUploadResult> {
  // Buffered points of a stored session go only to their own tenant (round 7 L1): the field-ingest
  // credential is minted for the ACTIVE tenant, so a tenant switch since the gate keeps them
  // pending (a transient failure, retried) instead of sending them to the new tenant.
  if (config?.tenantId && useTenantStore.getState().activeTenant?.id !== config.tenantId) {
    throw new Error('TENANT_CHANGED: these points belong to another tenant than the active one; kept for it');
  }
  if (isFieldIngestV1Enabled() && canSendAsEvents(points)) {
    const result = await uploadPositionEvents(sessionId, points);
    if (result) return result;
  }
  return flushPositionsLegacy(sessionId, points, config);
}

/** The pre-P1-12 route (POST /personnel-tracking/positions). Fallback only. */
export async function flushPositionsLegacy(
  sessionId: string,
  points: PositionRecord[],
  config?: SendConfig
): Promise<PositionUploadResult> {
  const body = { session_id: sessionId, positions: points };
  // A stored session's points pin their own tenant; a live flush goes to the active one.
  return config ? api.post<PositionUploadResult>('/personnel-tracking/positions', body, config) : api.post<PositionUploadResult>('/personnel-tracking/positions', body);
}

/** Split points into request-sized chunks, preserving order. */
export function chunkPositions<T>(points: T[], size: number = MAX_POSITIONS_PER_REQUEST): T[][] {
  if (size < 1) throw new Error('chunk size must be >= 1');
  const chunks: T[][] = [];
  for (let i = 0; i < points.length; i += size) {
    chunks.push(points.slice(i, i + size));
  }
  return chunks;
}

export type UploadFailureKind = 'transient' | 'dead_letter';

/** HTTP status of a failed upload, or undefined when no response arrived. */
export function uploadErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const e = error as { response?: { status?: unknown }; status?: unknown };
  const status = e.response?.status ?? e.status;
  return typeof status === 'number' ? status : undefined;
}

/**
 * Decide whether a failed upload may be retried.
 *
 *  - No response (offline, timeout, DNS) or 5xx: transient. Keep the points
 *    and retry later.
 *  - 401 (token expired), 403 (permission or tenant gate, which an admin
 *    can lift), 408 (request timeout), 429 (rate limited): transient. The
 *    same request can succeed later.
 *  - Any other 4xx: dead letter. The server rejected this payload for good:
 *    400 validation, 404 session gone, 413 too large. Retrying it forever
 *    wastes battery and data, and a poison chunk would block the queue. The
 *    points are set aside, not deleted.
 */
export function classifyUploadError(error: unknown): UploadFailureKind {
  // The field endpoint's "keep it and re-send" answers (409 PARENT_PENDING,
  // 429, 503, a retryable per-event rejection) are flagged by the client.
  if (error && typeof error === 'object' && (error as { retryable?: unknown }).retryable === true) {
    return 'transient';
  }
  const status = uploadErrorStatus(error);
  if (status === undefined) return 'transient';
  if (status === 401 || status === 403 || status === 408 || status === 429) return 'transient';
  if (status >= 400 && status < 500) return 'dead_letter';
  return 'transient';
}

export interface ChunkedUploadOutcome<T extends PositionRecord> {
  /** Points the server answered for (2xx): stored, duplicate or rejected by its clock window. */
  delivered: T[];
  /** Points to keep and retry: the failing chunk and every chunk after it. */
  retry: T[];
  /** Chunks the server refused with a permanent 4xx. Never send them again. */
  deadLettered: Array<{ points: T[]; status: number | undefined }>;
  /** Number of requests made. */
  requests: number;
}

/**
 * Upload points in chunks of at most MAX_POSITIONS_PER_REQUEST, in order.
 *
 * On a transient failure, the remaining chunks are not attempted: the
 * network is probably down, and the next sync resends them with the same
 * point_ids, which the server deduplicates. A dead-lettered chunk is set
 * aside and the next chunk is still sent, so one poison chunk cannot hold
 * back the rest.
 */
export async function uploadPositionsInChunks<T extends PositionRecord>(
  sessionId: string,
  points: T[],
  upload: (sessionId: string, chunk: T[]) => Promise<unknown> = flushPositions
): Promise<ChunkedUploadOutcome<T>> {
  const outcome: ChunkedUploadOutcome<T> = { delivered: [], retry: [], deadLettered: [], requests: 0 };
  const chunks = chunkPositions(points);

  for (let i = 0; i < chunks.length; i++) {
    const chunk = chunks[i];
    outcome.requests++;
    try {
      await upload(sessionId, chunk);
      outcome.delivered.push(...chunk);
    } catch (error) {
      if (classifyUploadError(error) === 'dead_letter') {
        outcome.deadLettered.push({ points: chunk, status: uploadErrorStatus(error) });
        continue;
      }
      for (let j = i; j < chunks.length; j++) outcome.retry.push(...chunks[j]);
      break;
    }
  }

  return outcome;
}
